import { useKeycloak } from '@react-keycloak/web';
import { CrudContext } from 'devyx-ui';
import React, { lazy, useMemo } from 'react';
import { Routes as RoutesList, Route, useNavigate } from 'react-router-dom';
import { CachePolicies, Provider as HttpProvider } from 'use-http';
import Footer from '../components/layout/Footer';
import Header from '../components/layout/Header';
import Login from '../pages/Auth/Login';
import Logout from '../pages/Auth/Logout';
import PrivateRoute from './PrivateRoute';

const Home = lazy(() => import('../pages/Dashboard'));
const Users = lazy(() => import('../modules/Users/Users.routes'));
const CompaniesPage = lazy(
  () => import('../modules/Companies/pages/CompaniesPage'),
);
const Profile = lazy(() => import('../pages/Profile'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));
const ProductsPage = lazy(() => import('../modules/Products/Products.routes'));
const ReportsPage = lazy(() => import('../modules/Reports/Reports.routes'));
const ConfigurationsPage = lazy(
  () => import('../modules/Configurations/Configurations.routes'),
);
const Terms = lazy(() => import('../pages/UseTerms'));
const PlanApprovals = lazy(
  () => import('../modules/PlanApprovals/PlanApprovalsPage'),
);

function Routes() {
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();

  const { setOpenAddCrudModal } = CrudContext.useCrud();

  // Memoiza optionsProvider para evitar recriação a cada render
  const optionsProvider = useMemo(
    () => ({
      cachePolicy: CachePolicies.CACHE_AND_NETWORK,
      interceptors: {
        request: async ({ options, url }: any) => {
          const newOptions = options;
          if (keycloak?.authenticated && keycloak?.token) {
            if (!url.includes('ibge') && !url.includes('viacep')) {
              newOptions.headers.authorization = `Bearer ${keycloak.token}`;
            }
          }
          return newOptions;
        },
        response: ({ response }: any) => {
          const res = response;

          if (res.status === 401) navigate('/logout');

          return res;
        },
      },
    }),
    [keycloak?.authenticated, keycloak?.token, navigate],
  );

  return (
    <HttpProvider
      url={process.env.REACT_APP_BACKEND_URL}
      options={optionsProvider}
    >
      <Header />
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
          flex: '1',
          width: '100%',
          paddingBottom: '80px',
        }}
      >
        <RoutesList>
          <Route path="/login" element={<Login />} />

          <Route
            path="/"
            element={
              <PrivateRoute>
                <Home />
              </PrivateRoute>
            }
          />

          <Route
            path="/users/*"
            element={
              <PrivateRoute>
                <Users setOpenAddCrudModal={setOpenAddCrudModal} />
              </PrivateRoute>
            }
          />

          <Route
            path="/companies/*"
            element={
              <PrivateRoute>
                <CompaniesPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/configurations/*"
            element={
              <PrivateRoute>
                <ConfigurationsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/products/*"
            element={
              <PrivateRoute>
                <ProductsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/reports/*"
            element={
              <PrivateRoute>
                <ReportsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/plan-approvals"
            element={
              <PrivateRoute>
                <PlanApprovals />
              </PrivateRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />

          <Route
            path="/logout"
            element={
              <PrivateRoute>
                <Logout />
              </PrivateRoute>
            }
          />

          <Route
            path="/terms"
            element={
              <PrivateRoute>
                <Terms />
              </PrivateRoute>
            }
          />

          <Route
            path="*"
            element={
              <PrivateRoute>
                <NotFoundPage />
              </PrivateRoute>
            }
          />
        </RoutesList>
      </div>
      <Footer />
    </HttpProvider>
  );
}

export default Routes;
